import React, { useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { setLoading, setSnackInfo } from "../redux/actions/appAction";
import {
    Card,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Chip,
    Typography,
    Box,
    Paper,
    CircularProgress,
    TablePagination,
    Tabs,
    Tab,
    Badge,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CheckIcon from "@material-ui/icons/Check";
import CancelIcon from "@material-ui/icons/Cancel";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import WatchLaterIcon from "@material-ui/icons/WatchLater";
import HistoryIcon from "@material-ui/icons/History";
import DescriptionIcon from "@material-ui/icons/Description";
import { APPROVAL_TYPES } from "../config/approvalTypesRegistry";

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100, 200, 500, 1000];
const DEFAULT_ROWS_PER_PAGE = 100;

const useStyles = makeStyles((theme) => ({
    card: {
        borderRadius: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    cardHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        padding: theme.spacing(2, 3),
        borderBottom: "1px solid #f0f0f0",
        flexWrap: "wrap",
        gap: theme.spacing(1),
    },
    cardContent: {
        padding: theme.spacing(3),
    },
    amountChip: {
        fontWeight: 700,
        color: "#1A3C6E",
    },
    actionButton: {
        borderRadius: theme.spacing(2),
        padding: theme.spacing(1, 3),
        fontWeight: 600,
        textTransform: "none",
        "&.approve": {
            backgroundColor: "#4caf50",
            color: "#fff",
            "&:hover": { backgroundColor: "#388e3c" },
        },
        "&.reject": {
            backgroundColor: "#f44336",
            color: "#fff",
            "&:hover": { backgroundColor: "#c62828" },
        },
    },
    emptyState: {
        padding: theme.spacing(8),
        textAlign: "center",
        borderRadius: theme.spacing(2),
    },
    loadingOverlay: {
        display: "flex",
        justifyContent: "center",
        padding: theme.spacing(8),
    },
    paginationContainer: {
        marginTop: theme.spacing(2),
        backgroundColor: "#fff",
        borderRadius: theme.spacing(1),
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    },
    tabsPaper: {
        marginBottom: theme.spacing(2),
        borderRadius: theme.spacing(1),
    },
    tabLabel: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
    },
    badge: {
        marginLeft: theme.spacing(1),
    },
}));

// Rows whose status still needs a decision - anything else counts as history.
const PENDING_STATUSES = ["pending_verification", "pending"];

const statusChip = (row) => {
    const status = row?.status;
    if (status === "rejected") {
        return <Chip size="small" label="Rejected" style={{ backgroundColor: "#ffebee", color: "#c62828", fontWeight: 600 }} />;
    }
    if (status === "active" || status === "approved") {
        return <Chip size="small" label="Approved" style={{ backgroundColor: "#e8f5e9", color: "#2e7d32", fontWeight: 600 }} />;
    }
    if (status) {
        return <Chip size="small" label={String(status).replace(/_/g, " ")} variant="outlined" />;
    }
    return <Chip size="small" label="Processed" variant="outlined" />;
};

// Self-contained "pending my approval" + "history" card list, pagination, and
// approve/reject dialog, driven by config/approvalTypesRegistry.js. Used both
// by the standalone ApprovalCenter page and embedded inline wherever a
// non-MiscPayment approval type needs to show up (e.g. the Misc Payment
// dashboard's "Other Payment Type" view, and the Onboarding dashboard's
// "Other Onboarding Approval" view).
//
// `typeKey="all"` merges every registered type's queue into one combined,
// paginated list - each row still uses its own type's icon/title/amount/card
// body/approve action, so adding a new type to the registry automatically
// shows up here too, no changes needed in this file.
const ExternalApprovalQueue = ({ typeKey, types, onStatsChange, setLoading: setLoadingProp, setSnackInfo: setSnackInfoProp }) => {
    const classes = useStyles();
    const [tabValue, setTabValue] = useState(0); // 0 = Pending, 1 = History

    const [pendingRows, setPendingRows] = useState([]); // [{ type, row }]
    const [pendingFetching, setPendingFetching] = useState(false);
    const [pendingPage, setPendingPage] = useState(0);
    const [pendingRowsPerPage, setPendingRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

    const [historyRows, setHistoryRows] = useState([]); // [{ type, row }]
    const [historyFetching, setHistoryFetching] = useState(false);
    const [historyPage, setHistoryPage] = useState(0);
    const [historyRowsPerPage, setHistoryRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogStatus, setDialogStatus] = useState("");
    const [remark, setRemark] = useState("");
    const [selectedEntry, setSelectedEntry] = useState(null); // { type, row }
    const [actionLoading, setActionLoading] = useState(false);

    const [detailsOpen, setDetailsOpen] = useState(false);
    const [detailsEntry, setDetailsEntry] = useState(null); // { type, row }

    const registry = types || APPROVAL_TYPES;
    const isAll = typeKey === "all";
    const activeTypes = useMemo(
        () => (isAll ? registry : registry.filter((t) => t.key === typeKey)),
        [typeKey, isAll, registry]
    );

    const fetchPending = async () => {
        if (activeTypes.length === 0) {
            setPendingRows([]);
            return;
        }
        setPendingFetching(true);
        try {
            const perType = await Promise.all(
                activeTypes.map(async (type) => {
                    try {
                        const response = await type.fetchApprovals();
                        return (response?.data?.data || []).map((row) => ({ type, row }));
                    } catch (error) {
                        console.error(`Failed to fetch ${type.key} approvals:`, error);
                        return [];
                    }
                })
            );
            setPendingRows(perType.flat());
        } catch (error) {
            setSnackInfoProp(error?.response?.data?.message || "Something went wrong", "error");
            setPendingRows([]);
        }
        setPendingFetching(false);
    };

    const fetchHistory = async () => {
        const typesWithHistory = activeTypes.filter((type) => typeof type.fetchHistory === "function");
        if (typesWithHistory.length === 0) {
            setHistoryRows([]);
            return;
        }
        setHistoryFetching(true);
        try {
            const perType = await Promise.all(
                typesWithHistory.map(async (type) => {
                    try {
                        const response = await type.fetchHistory();
                        const rows = response?.data?.data || [];
                        return rows
                            .filter((row) => !PENDING_STATUSES.includes(row?.status))
                            .map((row) => ({ type, row }));
                    } catch (error) {
                        console.error(`Failed to fetch ${type.key} history:`, error);
                        return [];
                    }
                })
            );
            setHistoryRows(perType.flat());
        } catch (error) {
            setSnackInfoProp(error?.response?.data?.message || "Something went wrong", "error");
            setHistoryRows([]);
        }
        setHistoryFetching(false);
    };

    useEffect(() => {
        fetchPending();
        fetchHistory();
        setPendingPage(0);
        setHistoryPage(0);
        setTabValue(0);
    }, [typeKey]);

    useEffect(() => {
        if (typeof onStatsChange !== "function") return;
        const approved = historyRows.filter(({ row }) => row?.status === "active" || row?.status === "approved").length;
        const rejected = historyRows.filter(({ row }) => row?.status === "rejected").length;
        const pending = pendingRows.length;
        onStatsChange({ pending, approved, rejected, total: pending + approved + rejected });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingRows, historyRows]);

    if (activeTypes.length === 0) {
        return null;
    }

    const openDialog = (entry, status) => {
        setSelectedEntry(entry);
        setDialogStatus(status);
        setRemark("");
        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setRemark("");
    };

    const openDetails = (entry) => {
        setDetailsEntry(entry);
        setDetailsOpen(true);
    };

    const closeDetails = () => {
        setDetailsOpen(false);
    };

    const submitDecision = async () => {
        if (!selectedEntry) return;
        setActionLoading(true);
        try {
            const { type, row } = selectedEntry;
            const response = await type.approveAction(type.approveId(row), {
                status: dialogStatus,
                action: dialogStatus.toLowerCase(),
                remarks: remark,
            });
            if (response?.data?.success) {
                setSnackInfoProp(response.data.message || "Updated successfully", "success");
            } else {
                setSnackInfoProp(response?.data?.message || "Failed to update", "error");
            }
        } catch (error) {
            setSnackInfoProp(error?.response?.data?.message || "Something went wrong", "error");
        }
        setActionLoading(false);
        closeDialog();
        fetchPending();
        fetchHistory();
    };

    const pagedPendingRows = pendingRows.slice(
        pendingPage * pendingRowsPerPage,
        pendingPage * pendingRowsPerPage + pendingRowsPerPage
    );
    const pagedHistoryRows = historyRows.slice(
        historyPage * historyRowsPerPage,
        historyPage * historyRowsPerPage + historyRowsPerPage
    );
    const emptyStateLabel = isAll ? "any payment type" : activeTypes[0].label;

    const renderCard = (type, row, isHistory) => (
        <Card
            key={`${type.key}_${type.rowKey(row)}`}
            className={classes.card}
            style={{ cursor: "pointer" }}
            onClick={() => openDetails({ type, row })}
        >
            <div className={classes.cardHeader}>
                <Box>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        {type.icon}
                        <Typography variant="h6" style={{ fontWeight: 600 }}>
                            {type.title(row)}
                        </Typography>
                        <Chip size="small" label={type.label} variant="outlined" style={{ fontSize: "0.7rem" }} />
                    </Box>
                </Box>
                <Box display="flex" alignItems="center" style={{ gap: 24 }}>
                    {typeof type.amount === "function" && (
                        <Typography variant="h6" className={classes.amountChip}>
                            ₹{Number(type.amount(row) || 0).toLocaleString()}
                        </Typography>
                    )}
                    {isHistory ? (
                        statusChip(row)
                    ) : (
                        <Box display="flex" style={{ gap: 8 }}>
                            <Button
                                size="small"
                                className={`${classes.actionButton} approve`}
                                startIcon={<CheckIcon />}
                                onClick={(e) => { e.stopPropagation(); openDialog({ type, row }, "Approved"); }}
                            >
                                Approve
                            </Button>
                            <Button
                                size="small"
                                className={`${classes.actionButton} reject`}
                                startIcon={<CancelIcon />}
                                onClick={(e) => { e.stopPropagation(); openDialog({ type, row }, "Rejected"); }}
                            >
                                Reject
                            </Button>
                        </Box>
                    )}
                </Box>
            </div>
            <div className={classes.cardContent}>
                {type.renderCardBody(row, classes)}
            </div>
        </Card>
    );

    return (
        <div>
            <Paper className={classes.tabsPaper}>
                <Tabs
                    value={tabValue}
                    onChange={(e, newValue) => setTabValue(newValue)}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="fullWidth"
                >
                    <Tab
                        label={
                            <div className={classes.tabLabel}>
                                <WatchLaterIcon fontSize="small" />
                                Pending
                                <Badge badgeContent={pendingRows.length} color="secondary" className={classes.badge} />
                            </div>
                        }
                    />
                    <Tab
                        label={
                            <div className={classes.tabLabel}>
                                <HistoryIcon fontSize="small" />
                                History
                                <Badge badgeContent={historyRows.length} color="primary" className={classes.badge} />
                            </div>
                        }
                    />
                </Tabs>
            </Paper>

            {tabValue === 0 ? (
                <>
                    {pendingFetching ? (
                        <div className={classes.loadingOverlay}>
                            <CircularProgress />
                        </div>
                    ) : pendingRows.length === 0 ? (
                        <Paper className={classes.emptyState}>
                            <AssignmentTurnedInIcon style={{ fontSize: 60, color: "#4caf50", marginBottom: 16 }} />
                            <Typography variant="h6">Nothing pending your approval</Typography>
                            <Typography variant="body2" color="textSecondary">
                                You're all caught up for {emptyStateLabel}.
                            </Typography>
                        </Paper>
                    ) : (
                        pagedPendingRows.map(({ type, row }) => renderCard(type, row, false))
                    )}

                    {pendingRows.length > 0 && (
                        <div className={classes.paginationContainer}>
                            <TablePagination
                                component="div"
                                count={pendingRows.length}
                                page={pendingPage}
                                onPageChange={(e, newPage) => setPendingPage(newPage)}
                                rowsPerPage={pendingRowsPerPage}
                                onRowsPerPageChange={(e) => { setPendingRowsPerPage(parseInt(e.target.value, 10)); setPendingPage(0); }}
                                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                                labelRowsPerPage="Rows per page:"
                            />
                        </div>
                    )}
                </>
            ) : (
                <>
                    {historyFetching ? (
                        <div className={classes.loadingOverlay}>
                            <CircularProgress />
                        </div>
                    ) : historyRows.length === 0 ? (
                        <Paper className={classes.emptyState}>
                            <HistoryIcon style={{ fontSize: 60, color: "#9e9e9e", marginBottom: 16 }} />
                            <Typography variant="h6">No history yet</Typography>
                            <Typography variant="body2" color="textSecondary">
                                Approved or rejected records for {emptyStateLabel} will show up here.
                            </Typography>
                        </Paper>
                    ) : (
                        pagedHistoryRows.map(({ type, row }) => renderCard(type, row, true))
                    )}

                    {historyRows.length > 0 && (
                        <div className={classes.paginationContainer}>
                            <TablePagination
                                component="div"
                                count={historyRows.length}
                                page={historyPage}
                                onPageChange={(e, newPage) => setHistoryPage(newPage)}
                                rowsPerPage={historyRowsPerPage}
                                onRowsPerPageChange={(e) => { setHistoryRowsPerPage(parseInt(e.target.value, 10)); setHistoryPage(0); }}
                                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                                labelRowsPerPage="Rows per page:"
                            />
                        </div>
                    )}
                </>
            )}

            <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="xs" fullWidth PaperProps={{ style: { borderRadius: 16 } }}>
                <DialogTitle>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        {dialogStatus === "Approved" ? (
                            <CheckIcon style={{ color: "#4caf50" }} />
                        ) : (
                            <CancelIcon style={{ color: "#f44336" }} />
                        )}
                        {dialogStatus === "Approved" ? "Approve" : "Reject"} Request
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <TextField
                        label="Remark"
                        variant="outlined"
                        value={remark}
                        onChange={(e) => setRemark(e.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                        margin="dense"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog} disabled={actionLoading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={submitDecision}
                        variant="contained"
                        color={dialogStatus === "Approved" ? "primary" : "secondary"}
                        disabled={actionLoading}
                    >
                        {actionLoading ? <CircularProgress size={18} color="inherit" /> : "Confirm"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={detailsOpen} onClose={closeDetails} maxWidth="md" fullWidth PaperProps={{ style: { borderRadius: 16 } }}>
                {detailsEntry && (
                    <>
                        <DialogTitle>
                            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" style={{ gap: 8 }}>
                                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                    {detailsEntry.type.icon}
                                    {detailsEntry.type.title(detailsEntry.row)}
                                    <Chip size="small" label={detailsEntry.type.label} variant="outlined" style={{ fontSize: "0.7rem" }} />
                                </Box>
                                {statusChip(detailsEntry.row)}
                            </Box>
                        </DialogTitle>
                        <DialogContent dividers>
                            {typeof detailsEntry.type.renderDetails === "function"
                                ? detailsEntry.type.renderDetails(detailsEntry.row, { classes, setSnackInfo: setSnackInfoProp })
                                : detailsEntry.type.renderCardBody(detailsEntry.row, classes)}

                            {(() => {
                                const docs = typeof detailsEntry.type.documents === "function"
                                    ? (detailsEntry.type.documents(detailsEntry.row) || [])
                                    : [];
                                if (docs.length === 0) return null;
                                return (
                                    <Box mt={3}>
                                        <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
                                            Documents
                                        </Typography>
                                        <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                                            {docs.map((doc) => (
                                                <Button
                                                    key={doc.label}
                                                    variant="outlined"
                                                    color="primary"
                                                    size="small"
                                                    component="a"
                                                    href={doc.url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    startIcon={<DescriptionIcon />}
                                                >
                                                    {doc.label}
                                                </Button>
                                            ))}
                                        </Box>
                                    </Box>
                                );
                            })()}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={closeDetails}>Close</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </div>
    );
};

export default connect(null, { setLoading, setSnackInfo })(ExternalApprovalQueue);
